/**
 * Graphe d'ÉVOLUTION au survol d'une stat. Un seul contrôleur global écoute les
 * éléments `[data-stat]` (lignes de stat du panneau de match et de la carte
 * « En direct ») ; au survol, il retrouve le match via l'ancêtre `[data-match]`,
 * récupère la série temporelle (statsHistory) et affiche un mini-graphe dans une
 * bulle fixe `pointer-events:none` (comme le tooltip de parcours), placée à côté
 * de la ligne.
 *
 * Au survol (desktop) ou au TAP (mobile, et dans l'overlay détail au clic) sur
 * une ligne de stat, le graphe s'ouvre ; un nouveau tap sur la même ligne — ou
 * un clic ailleurs — le referme. La carte live fonctionne au survol direct.
 */
import { t } from "../app/i18n.ts";
import type { OfficialState } from "../app/officialStore.ts";
import { fetchStatHistory, pluckSeries } from "../app/statsHistory.ts";
import { statGraph } from "./components/statGraph.ts";

let getState: (() => OfficialState | null) | null = null;
let tip: HTMLElement | null = null;
let installed = false;
let activeRow: HTMLElement | null = null;
let token = 0; // anti-course : invalide les fetchs dont le survol a changé

function ensureTip(): HTMLElement {
  if (!tip) {
    tip = document.createElement("div");
    tip.className = "sg-tip hide";
    document.body.appendChild(tip);
  }
  return tip;
}

const isCode = (c: string): boolean => /^[A-Z]{3}$/.test(c);

/** Codes domicile/extérieur d'un match (résolus via le tableau pour les KO). */
function codesFor(state: OfficialState | null, id: number): [string, string] {
  const v = state?.byId.get(id);
  let home = v?.match.home ?? "DOM";
  let away = v?.match.away ?? "EXT";
  if (state && v && v.match.stage !== "group") {
    const node = state.bracket["M" + String(id)];
    if (node?.a) home = node.a;
    if (node?.b) away = node.b;
  }
  return [isCode(home) ? home : "DOM", isCode(away) ? away : "EXT"];
}

function place(anchor: HTMLElement, el: HTMLElement): void {
  const r = anchor.getBoundingClientRect();
  el.style.left = "0px";
  el.style.top = "0px";
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  let left = r.right + 10; // à droite de la ligne…
  if (left + w > window.innerWidth - 8) left = r.left - w - 10; // …sinon à gauche
  left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
  let top = r.top + r.height / 2 - h / 2;
  top = Math.max(8, Math.min(top, window.innerHeight - h - 8));
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}

async function show(row: HTMLElement): Promise<void> {
  if (row === activeRow && tip && !tip.classList.contains("hide")) return;
  activeRow = row;
  const key = row.dataset.stat;
  const matchEl = row.closest<HTMLElement>("[data-match]");
  if (!key || !matchEl) return;
  const id = Number(matchEl.dataset.match);
  if (!Number.isInteger(id)) return;
  const state = getState?.() ?? null;
  const v = state?.byId.get(id);
  const live = v?.status === "live" || v?.status === "paused";
  const [home, away] = codesFor(state, id);
  const goals = (v?.live?.events ?? [])
    .filter((e) => e.type === "goal" && typeof e.minute === "number")
    .map((e) => ({ minute: e.minute as number, team: e.team }));
  const mine = ++token;
  const el = ensureTip();
  el.innerHTML = `<div class="sg-title">…</div><div class="sg-empty">${t("common.loading")}</div>`;
  el.classList.remove("hide");
  place(row, el);
  const series = await fetchStatHistory(id, { live });
  if (mine !== token) return; // un autre survol a pris la main
  el.innerHTML = statGraph(pluckSeries(series, key), key, home, away, goals, live);
  place(row, el);
}

function hide(): void {
  activeRow = null;
  token++;
  tip?.classList.add("hide");
}

/** Installe le contrôleur (idempotent). `get` fournit l'état officiel courant. */
export function registerStatGraph(get: () => OfficialState | null): void {
  getState = get;
  if (installed) return;
  installed = true;
  document.addEventListener("mouseover", (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const row = t.closest<HTMLElement>("[data-stat]");
    if (row) void show(row);
  });
  document.addEventListener("mouseout", (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const row = t.closest<HTMLElement>("[data-stat]");
    if (!row) return;
    const related = e instanceof MouseEvent ? e.relatedTarget : null;
    if (related instanceof Element) {
      if (row.contains(related)) return; // toujours dans la même ligne
      if (related.closest("[data-stat]")) return; // passe à une autre stat → géré par mouseover
    }
    hide();
  });
  // TAP : pas de survol au doigt → on bascule le graphe au clic sur une stat
  // (et un clic ailleurs le ferme). Indispensable sur mobile et dans l'overlay détail.
  document.addEventListener("click", (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const row = t.closest<HTMLElement>("[data-stat]");
    if (!row) {
      hide();
      return;
    }
    const shown = tip !== null && !tip.classList.contains("hide");
    if (activeRow === row && shown) hide();
    else void show(row);
  });
}
